import {
  IsNotEmpty,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
} from 'class-validator';

/**
 * A transcript chunk sent during recording for incremental anonymization.
 * The backend forwards it to Seranote `/api/anonymize-and-correct` and returns
 * the cleaned text; the client accumulates the results and sends them back as
 * `anonymizedTranscription` on the final dictation request.
 */
export class SeranoteAnonymizeRequestDto {
  // Seranote caps each anonymize request at 90KB. Chunks are ~150 words, so
  // this cap is a backstop, not a working limit.
  @IsString()
  @IsNotEmpty()
  @MaxLength(80_000)
  transcription: string;
}

export interface SeranoteAnonymizeResponseDto {
  content: string;
}

/**
 * Sent from Capture on `Stop`. `transcription` is the raw, not-yet-anonymized
 * tail of the live ASR output; `anonymizedTranscription` is the accumulated
 * output of prior `/seranote/anonymize` chunk calls in this session. The
 * backend anonymizes the tail, prepends the accumulated text, and forwards the
 * whole thing to Seranote `/api/dictation`. At least one of the two must be
 * non-empty (enforced in the controller).
 */
export class SeranoteDictationRequestDto {
  @IsOptional()
  @IsString()
  @MaxLength(80_000)
  transcription?: string;

  @IsOptional()
  @IsString()
  @MaxLength(80_000)
  anonymizedTranscription?: string;

  @IsUUID()
  conditionId: string;

  @IsOptional()
  @IsUUID()
  conditionTriageId?: string;
}

export interface SeranoteDictationResponseDto {
  content: string;
}

/**
 * Response of GET /seranote/stream-token. The RN client passes `token` and
 * `sessionUuid` into the socket.io `auth` object when opening the Seranote
 * transcription WebSocket. The token has limited lifetime — refresh by
 * calling this endpoint again, no full app re-auth needed.
 */
export interface SeranoteStreamTokenResponseDto {
  /** Seranote Keycloak access token (used as `auth.token` in socket.io). */
  token: string;

  /** Epoch milliseconds. Mobile should re-fetch the token before this elapses. */
  expiresAt: number;

  /** Server-issued Seranote session id. Lets the client reconnect within the 60s window. */
  sessionUuid: string;

  /** wss://… URL for the Seranote transcription socket. */
  wsUrl: string;
}
